/**
 * useProfile.js
 * Minimal custom hook for user profile fetch/save operations
 * 
 * STRANGLER PATTERN: This hook ONLY handles profile CRUD operations.
 * All side effects (subscription, goals, intelligence level, etc.) 
 * remain in App.js and are called via an orchestrator function.
 * 
 * Exposes:
 * - userProfile: The current profile data
 * - profileLoaded: Boolean indicating if initial load completed
 * - profileLoading: Boolean indicating if a fetch is in progress
 * - profileError: Error message if fetch failed
 * - refreshProfile: Function to fetch profile from backend
 * - saveProfile: Function to save profile to backend
 * - setUserProfile: Direct setter for external updates
 */

import { useState, useCallback, useRef, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Custom hook for user profile CRUD operations only
 * @returns {object} Profile state and functions
 */
export const useProfile = () => {
  // State
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);

  // Ref for tracking mounted state (defensive cleanup)
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Abort any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Fetch user profile from backend
   * Returns the profile data for orchestrator to process side effects
   * @param {string} uid - User ID to fetch profile for
   * @returns {Promise<{data: object|null, exists: boolean}>}
   */
  const refreshProfile = useCallback(async (uid) => {
    if (!uid) {
      return { data: null, exists: false };
    }

    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      if (isMountedRef.current) {
        setProfileLoading(true);
        setProfileError(null);
      }

      const response = await fetch(
        `${BACKEND_URL}/api/users/${uid}/profile`,
        { signal: abortControllerRef.current.signal }
      );

      if (!isMountedRef.current) {
        return { data: null, exists: false };
      }

      if (response.ok) {
        const data = await response.json();
        setUserProfile(data);
        return { data, exists: true };
      } else {
        // No profile found (404) or other error
        setUserProfile(null);
        return { data: null, exists: false };
      }
    } catch (error) {
      // Don't log abort errors
      if (error.name === 'AbortError') {
        return { data: null, exists: false };
      }
      
      console.error('Error loading profile:', error);
      if (isMountedRef.current) {
        setProfileError('Failed to load profile');
      }
      return { data: null, exists: false };
    } finally {
      if (isMountedRef.current) {
        setProfileLoading(false);
        setProfileLoaded(true);
      }
    }
  }, []);

  /**
   * Save user profile to backend
   * @param {object} profileData - Profile data to save (must include user_id)
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  const saveProfile = useCallback(async (profileData) => {
    if (!profileData?.user_id) {
      return { success: false, data: null, error: 'User ID required' };
    }

    try {
      if (isMountedRef.current) {
        setProfileLoading(true);
        setProfileError(null);
      }

      const response = await fetch(`${BACKEND_URL}/api/users/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileData)
      });

      if (!isMountedRef.current) {
        return { success: false, data: null, error: 'Component unmounted' };
      }

      if (response.ok) {
        const data = await response.json();
        return { success: true, data, error: null };
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.detail || 'Failed to save profile';
        setProfileError(errorMsg);
        return { success: false, data: null, error: errorMsg };
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      const errorMsg = 'Failed to save profile';
      if (isMountedRef.current) {
        setProfileError(errorMsg);
      }
      return { success: false, data: null, error: errorMsg };
    } finally {
      if (isMountedRef.current) {
        setProfileLoading(false);
      }
    }
  }, []);

  /**
   * Clear profile state (for logout)
   */
  const clearProfile = useCallback(() => {
    if (isMountedRef.current) {
      setUserProfile(null);
      setProfileLoaded(false);
      setProfileLoading(false);
      setProfileError(null);
    }
  }, []);

  return {
    // State
    userProfile,
    profileLoaded,
    profileLoading,
    profileError,
    
    // Functions
    refreshProfile,
    saveProfile,
    clearProfile,
    
    // Direct setter for external updates (e.g., after onboarding)
    setUserProfile,
    setProfileLoaded
  };
};

export default useProfile;
